import publicApi from './publicApi';

class PublicIconService {
  constructor() {
    this.iconCache = new Set(); // URLs that have been successfully loaded
    this.loadingIcons = new Set(); // URLs currently being loaded
    this.failedIcons = new Set(); // URLs that have permanently failed
    this.coinDataCache = new Map(); // Cache for coin data
    this.lastRefresh = 0; // Timestamp of last data refresh
    this.refreshInterval = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get all available icon URLs for a coin in order of preference
   * @param {string} coinSymbol - The coin symbol (e.g., 'BTC', 'ETH')
   * @returns {Array<string>} - Array of available URLs
   */
  getAvailableIconUrls(coinSymbol) {
    if (!coinSymbol) return [];
    
    const coin = this.coinDataCache.get(coinSymbol);
    if (!coin) return [];
    
    // Return all available URLs in order of preference
    return [coin.image_url, coin.image_url_2, coin.image_url_3].filter(Boolean);
  }

  /**
   * Get coin data for a specific symbol
   * @param {string} coinSymbol - The coin symbol
   * @returns {Object|null} - Coin data or null if not found
   */
  getCoinData(coinSymbol) {
    if (!coinSymbol) return null;
    return this.coinDataCache.get(coinSymbol) || null;
  }

  /**
   * Check if an icon URL is currently loading
   * @param {string} iconUrl - The icon URL to check
   * @returns {boolean} - True if loading
   */
  isIconLoading(iconUrl) {
    return this.loadingIcons.has(iconUrl);
  }

  /**
   * Check if an icon URL has been successfully loaded
   * @param {string} iconUrl - The icon URL to check
   * @returns {boolean} - True if loaded
   */
  isIconLoaded(iconUrl) {
    return this.iconCache.has(iconUrl);
  }

  /**
   * Check if an icon URL has failed to load
   * @param {string} iconUrl - The icon URL to check
   * @returns {boolean} - True if failed
   */
  isIconFailed(iconUrl) {
    return this.failedIcons.has(iconUrl);
  }

  /**
   * Load coin data from the public API
   * @returns {Promise<void>}
   */
  async loadCoinData() {
    try {
      const response = await publicApi.get('/api/public/coins');
      const coins = response.data || [];
      
      // Clear and rebuild cache
      this.coinDataCache.clear();
      coins.forEach(coin => {
        this.coinDataCache.set(coin.symbol, coin);
      });
      
      this.lastRefresh = Date.now();
    } catch (error) {
      console.error('[PublicIconService] Error loading coin data:', error);
    }
  }

  /**
   * Refresh coin data if needed
   * @returns {Promise<void>}
   */
  async refreshCoinDataIfNeeded() {
    const now = Date.now();
    if (!this.lastRefresh || (now - this.lastRefresh) > this.refreshInterval) {
      await this.loadCoinData();
    }
  }

  /**
   * Load an icon from a URL
   * @param {string} url - The icon URL to load
   * @returns {Promise<boolean>} - True if successful
   */
  async loadIcon(url) {
    if (!url) return false;
    
    // If already loaded, return true
    if (this.iconCache.has(url)) return true;
    
    // If already failed, return false
    if (this.failedIcons.has(url)) return false;
    
    // If currently loading, wait
    if (this.loadingIcons.has(url)) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (this.iconCache.has(url)) {
            resolve(true);
          } else if (this.failedIcons.has(url)) {
            resolve(false);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }
    
    // Mark as loading
    this.loadingIcons.add(url);
    
    try {
      // Test if the image loads successfully
      const success = await this.testUrlAccessibility(url);
      
      if (success) {
        this.iconCache.add(url);
        this.loadingIcons.delete(url);
        return true;
      } else {
        this.failedIcons.add(url);
        this.loadingIcons.delete(url);
        return false;
      }
    } catch (error) {
      this.failedIcons.add(url);
      this.loadingIcons.delete(url);
      return false;
    }
  }

  /**
   * Test if a URL is accessible by loading it as an image
   * @param {string} url - The URL to test
   * @returns {Promise<boolean>} - True if accessible
   */
  async testUrlAccessibility(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
      
      // Timeout after 5 seconds
      setTimeout(() => resolve(false), 5000);
    });
  }
}

// Create a singleton instance
const publicIconService = new PublicIconService();

export default publicIconService; 